import type { PurchaserConfigTemplate } from "@src/domain/entities/purchaser";

/**
 * Brand-scoped catalog config-template cache for Master Data Add Purchaser.
 * Survives section remounts until full browser refresh or template save.
 * `null` means fetched with no template — do not refetch.
 */
const persistentPurchaserConfigTemplateByBrandCache = new Map<
  string,
  PurchaserConfigTemplate | null
>();

const inflightPurchaserConfigTemplateLoads = new Map<
  string,
  Promise<PurchaserConfigTemplate | null>
>();

export function configTemplateCacheKey(
  brandId: number | null,
  brandKey: string,
  purchaserId: number,
): string {
  if (brandId != null) {
    return `${brandId}|${brandKey}|${purchaserId}`;
  }
  return `${brandKey}|${purchaserId}`;
}

export function hasCachedPurchaserConfigTemplate(
  brandId: number | null,
  brandKey: string,
  purchaserId: number,
): boolean {
  return persistentPurchaserConfigTemplateByBrandCache.has(
    configTemplateCacheKey(brandId, brandKey, purchaserId),
  );
}

/** `undefined` = miss; `null` = cached empty template. */
export function getCachedPurchaserConfigTemplate(
  brandId: number | null,
  brandKey: string,
  purchaserId: number,
): PurchaserConfigTemplate | null | undefined {
  const key = configTemplateCacheKey(brandId, brandKey, purchaserId);
  if (!persistentPurchaserConfigTemplateByBrandCache.has(key)) {
    return undefined;
  }
  return persistentPurchaserConfigTemplateByBrandCache.get(key) ?? null;
}

export function setCachedPurchaserConfigTemplate(
  brandId: number | null,
  brandKey: string,
  purchaserId: number,
  template: PurchaserConfigTemplate | null,
): void {
  persistentPurchaserConfigTemplateByBrandCache.set(
    configTemplateCacheKey(brandId, brandKey, purchaserId),
    template,
  );
}

export function invalidatePurchaserConfigTemplate(purchaserId: number): void {
  const suffix = `|${purchaserId}`;
  for (const key of persistentPurchaserConfigTemplateByBrandCache.keys()) {
    if (key.endsWith(suffix)) {
      persistentPurchaserConfigTemplateByBrandCache.delete(key);
    }
  }
  for (const key of inflightPurchaserConfigTemplateLoads.keys()) {
    if (key.endsWith(suffix)) {
      inflightPurchaserConfigTemplateLoads.delete(key);
    }
  }
}

export function getOrFetchPurchaserConfigTemplate(
  brandId: number | null,
  brandKey: string,
  purchaserId: number,
  fetch: () => Promise<PurchaserConfigTemplate | null>,
): Promise<PurchaserConfigTemplate | null> {
  const key = configTemplateCacheKey(brandId, brandKey, purchaserId);
  if (persistentPurchaserConfigTemplateByBrandCache.has(key)) {
    return Promise.resolve(
      persistentPurchaserConfigTemplateByBrandCache.get(key) ?? null,
    );
  }

  const pending = inflightPurchaserConfigTemplateLoads.get(key);
  if (pending) {
    return pending;
  }

  const request = fetch()
    .then((template) => {
      persistentPurchaserConfigTemplateByBrandCache.set(key, template);
      return template;
    })
    .finally(() => {
      inflightPurchaserConfigTemplateLoads.delete(key);
    });

  inflightPurchaserConfigTemplateLoads.set(key, request);
  return request;
}
